/**
 * Turning the funnel's live Rive character into flat PNGs.
 *
 * The posters and the reveal show an IMAGE, not a Rive canvas: one character has
 * to appear a dozen times at once (a shelf of posters), and mounting a dozen
 * WebGL contexts to do it is neither affordable nor necessary. So the stage
 * paints the child once and this module lifts the pixels off that canvas.
 *
 * ⚠ THE SAME-TASK RULE. `@rive-app/webgl2` creates its context with
 * `preserveDrawingBuffer: 0`, so the drawing buffer is valid only until the
 * browser composites — i.e. until the end of the current task. Every read here
 * must therefore run SYNCHRONOUSLY after `rive.drawFrame()`, in the same tick.
 * An `await` between the draw and the copy reliably yields a transparent image.
 * {@link copyRiveCanvas} is the only function that touches the GL canvas, and it
 * does no async work; everything after it operates on the 2D copy.
 */

/** A rect in image pixels. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Copy the WebGL canvas into a plain 2D canvas.
 *
 * MUST be called synchronously after `rive.drawFrame()` — see the same-task rule
 * above. Returns null if the canvas has no size yet.
 */
export function copyRiveCanvas(gl: HTMLCanvasElement | null | undefined): HTMLCanvasElement | null {
  if (!gl || !gl.width || !gl.height) return null;
  const out = document.createElement('canvas');
  out.width = gl.width;
  out.height = gl.height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(gl, 0, 0);
  return out;
}

/**
 * Where a Contain-fitted artboard actually lands on the canvas.
 *
 * Rive's own layout (what `resetRiveView` restores) fits the artboard inside the
 * canvas preserving aspect, so unless the two aspects match exactly there are
 * transparent letterbox bars. Mapping artboard coordinates onto the image needs
 * this rect, not the canvas rect.
 */
export function containRect(canvasW: number, canvasH: number, refW: number, refH: number): Rect {
  const fit = Math.min(canvasW / refW, canvasH / refH);
  const w = refW * fit;
  const h = refH * fit;
  return { x: (canvasW - w) / 2, y: (canvasH - h) / 2, w, h };
}

/** Map a rect in ARTBOARD units onto the image, given the drawn rect. */
export function artboardRectToImage(
  rect: Rect,
  drawn: Rect,
  refW: number,
  refH: number,
): Rect {
  const sx = drawn.w / refW;
  const sy = drawn.h / refH;
  return { x: drawn.x + rect.x * sx, y: drawn.y + rect.y * sy, w: rect.w * sx, h: rect.h * sy };
}

/**
 * The tight bounding box of everything more opaque than `alphaMin` (0…255).
 *
 * Returns null when the canvas is EMPTY — which is also how a failed capture
 * shows up (the same-task rule broken, or a frame that never painted), so
 * callers use a null here as "retry", not as "the child is invisible".
 */
export function alphaBounds(canvas: HTMLCanvasElement, alphaMin = 8): Rect | null {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const { width: w, height: h } = canvas;
  if (!w || !h) return null;
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      if (data[row + x * 4 + 3] < alphaMin) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Intersect a rect with the canvas, rounded to whole pixels. Null if empty. */
function clampRect(rect: Rect, w: number, h: number): Rect | null {
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  const right = Math.min(w, Math.ceil(rect.x + rect.w));
  const bottom = Math.min(h, Math.ceil(rect.y + rect.h));
  if (right <= x || bottom <= y) return null;
  return { x, y, w: right - x, h: bottom - y };
}

/** Crop to `rect` and encode as a PNG data URL (transparency preserved). */
export function cropToPng(canvas: HTMLCanvasElement, rect: Rect): string | null {
  const box = clampRect(rect, canvas.width, canvas.height);
  if (!box) return null;
  const out = document.createElement('canvas');
  out.width = box.w;
  out.height = box.h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
  return out.toDataURL('image/png');
}

/**
 * The character's two published forms.
 *
 * `full` is the whole standing figure trimmed to its own silhouette, so a poster
 * can place it by its feet without guessing where the transparent margin ended.
 * `head` is the editor's own head framing — the face big and centred, which is
 * what an avatar bubble wants.
 */
export interface AvatarShots {
  full: string;
  head: string;
  /**
   * The head alone — hair to chin, no shoulders — with the neck feathered out,
   * for the costume plates: a painted body in an outfit with this dropped onto
   * it. Null when the collar could not be found (an unexpected rig).
   */
  face: string | null;
  /** Aspect (w / h) of `full`, so posters can size it without waiting for a load. */
  fullAspect: number;
  /** Aspect (w / h) of `face`, for the same reason. */
  faceAspect: number;
}

/**
 * The row where the rig's baked sweater begins, in canvas px, or null.
 *
 * The costume is painted into the rig (a yellow jumper), so its colour is a
 * fixed fact of the file: the first row inside the silhouette where saturated
 * sweater-yellow covers a quarter of the width is the collar, whatever the hair
 * style above it does to the bounds. Skin and hair never reach that hue.
 */
/**
 * The rig's sweater yellow (#FDBF22-ish), and nothing else the rig paints:
 * ginger hair (#F7B65B) and the warm ring of a brown iris both sit close, so
 * the blue channel is the tell — the sweater has almost none.
 */
const isSweater = (r: number, g: number, b: number) => r > 240 && g > 175 && g < 215 && b < 60;

/**
 * The jumper's OUTLINE, which is a different colour from the jumper.
 *
 * The yellow fringe along the bottom of every cut-out face was not the sweater
 * fill at all — it was the darker orange stroke the rig draws around the
 * collar, around #D67600: saturated, and with no blue in it whatsoever. That
 * stroke sits a few pixels above the row where the fill first covers a quarter
 * of the width, so `collarRow` never reached it and `isSweater` never matched
 * it.
 *
 * Blue is still the tell, and it has to stay the tell: ginger hair (#F7B65B)
 * hangs into this same band and is otherwise nearly the same colour. It carries
 * b≈91, the outline carries none, so the cut goes between them.
 *
 * Only ever used inside the band at the bottom of the face crop, where nothing
 * but neck and collar can be.
 */
const isSweaterEdge = (r: number, g: number, b: number) => r > 190 && b < 70 && g < r - 45;

export function collarRow(canvas: HTMLCanvasElement, bounds: Rect): number | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const { data } = ctx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);
  const need = bounds.w * 0.25;
  for (let y = 0; y < bounds.h; y++) {
    let n = 0;
    for (let x = 0; x < bounds.w; x++) {
      const i = (y * bounds.w + x) * 4;
      const r = data[i]; const g = data[i + 1]; const b = data[i + 2]; const a = data[i + 3];
      if (a > 200 && isSweater(r, g, b)) n++;
    }
    if (n > need) return bounds.y + y;
  }
  return null;
}

/**
 * Crop the head above `collarY`, fading the last rows so the neck melts into
 * whatever collar the costume plate paints beneath it, and dropping any sweater
 * pixels that sit above the cut.
 */
export function cropFacePng(canvas: HTMLCanvasElement, bounds: Rect, collarY: number): string | null {
  // Cut ABOVE the collar row, not below it. `collarRow` finds where the jumper
  // FILL first covers a quarter of the width; its outline and the pixels where
  // it blends into the neck are already several rows higher, and no colour test
  // separates those blends from ginger hair reliably. Losing six rows of neck
  // costs nothing — the feather below re-creates the join — and it takes the
  // whole fringe with it.
  const box = clampRect({ x: bounds.x, y: bounds.y, w: bounds.w, h: collarY - 6 - bounds.y }, canvas.width, canvas.height);
  if (!box) return null;
  const out = document.createElement('canvas');
  out.width = box.w;
  out.height = box.h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
  const img = ctx.getImageData(0, 0, box.w, box.h);
  const d = img.data;
  // Only the feathered band at the bottom is touched: the fade, plus dropping
  // the sweater's ribbing that pokes above the cut. Never the face itself — an
  // eye highlight or a lock of hair that happens to be yellow-ish stays.
  const feather = Math.min(36, box.h);
  // The colour cut reaches a little higher than the fade does: the jumper's
  // ribbing pokes above the collar row, and its blended edge with it.
  const scrub = Math.min(feather + 14, box.h);
  for (let y = box.h - scrub; y < box.h; y++) {
    const fadeFrom = box.h - feather;
    const k = y < fadeFrom ? 1 : 1 - (y - fadeFrom) / feather;
    for (let x = 0; x < box.w; x++) {
      const i = (y * box.w + x) * 4;
      if (isSweaterEdge(d[i], d[i + 1], d[i + 2])) d[i + 3] = 0;
      else d[i + 3] = Math.round(d[i + 3] * k);
    }
  }
  ctx.putImageData(img, 0, 0);
  return out.toDataURL('image/png');
}
